import { FC, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/Interface/Button/Button";
import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { KYCWarning } from "@/components/Portal/Cockpit/UserWarning/KYCWarning/KYCWarning";
import { createIdentificationStatusQueryOptions } from "@/hooks/identificationStatusQueryOptions";
import {
  KYC_STATUS_ONBOARDING_NOT_FINISHED,
  useNeedsIDCheck,
} from "@/hooks/useNeedsIDCheck";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useGetIdentificationStatus } from "@/services/api";
import { IdentificationStatusResponseKycStatus } from "@/services/model";
import { isIdCheckPageActionable } from "@/utils/kycTodoConfig";

/**
 * Dedicated ID-check page: full KYC content and CTA that used to live inline
 * in the cockpit To-dos section.
 */
export const IdCheckPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromOrder = searchParams.get("from") === "order";
  const needsIdCheck = useNeedsIDCheck();
  const staleTime = useRef(0);
  const { data: identificationStatus } = useGetIdentificationStatus({
    query: createIdentificationStatusQueryOptions(staleTime),
  });

  if (!identificationStatus) {
    return (
      <section className="w-full pb-8" data-testid="id-check-page">
        <CardContainer
          variant="ivory"
          className="flex w-full flex-col gap-6 rounded-lg p-6"
        >
          <LoadingSpinner />
        </CardContainer>
      </section>
    );
  }

  if (
    identificationStatus.kycStatus ===
    IdentificationStatusResponseKycStatus.KYC_data_mismatch
  ) {
    return <Navigate to={portalRoutes.user.personal} replace />;
  }

  const shouldRedirectAway =
    needsIdCheck === false ||
    (identificationStatus.kycStatus as string | undefined) ===
      KYC_STATUS_ONBOARDING_NOT_FINISHED ||
    !isIdCheckPageActionable(identificationStatus.kycStatus);

  if (shouldRedirectAway) {
    return <Navigate to={portalRoutes.cockpit.base} replace />;
  }

  return (
    <section className="w-full pb-8" data-testid="id-check-page">
      <CardContainer
        variant="ivory"
        className="flex w-full flex-col gap-6 rounded-lg p-6"
      >
        {/* Text only, no chevron - matches the design's leave-for-later control. */}
        <Button
          dataTestid="id-check-back"
          className="self-start"
          variant="tertiary"
          onClick={() => navigate(portalRoutes.cockpit.base)}
        >
          {t("cockpit.idCheck.skipForLater")}
        </Button>

        <KYCWarning
          inlinePrep
          pageLayout
          fromOrder={
            fromOrder &&
            identificationStatus.kycStatus ===
              IdentificationStatusResponseKycStatus.OPEN
          }
        />
      </CardContainer>
    </section>
  );
};
